/** Angular imports */
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { delay, map, Observable, of, tap } from 'rxjs';

/** Local imports */

import { BaseQueryParams, ClientTypeCode } from '@shared';
import { buildQueryParams } from '@shared/utils';

/** Interfaces */
import { UiDealersAutocomplete } from '@features/users/interfaces';
import { ApiClientType } from '../interfaces';
import { ApiGetAllResponse, ApiSingleResponse } from '../interfaces/pagination.interface';
import { SharedService } from '../shared/shared.service';
import {
    ApiClientInformation,
    ApiDealer,
    ApiDealerHost,
    ApiDealerInformation,
    ApiDealerListResponse,
    ApiDealerNew,
    ApiDealerStats,
    DealerCacheType,
    DealerPatchBody,
    DealerStats,
} from './dealer.interface';

/**
 * Service responsible for managing dealer and advertiser information.
 *
 * @description
 * Handles API interactions for client information, host-dealer ownerships,
 * and client type resolutions. Implements caching for expensive paginated queries.
 */
@Injectable({
    providedIn: 'root',
})
export class DealerService {
    /**
     * Inject http instance for api calls
     */
    private _http = inject(HttpClient);
    /**
     * Shared service for common utilities.
     */
    private readonly _sharedService = inject(SharedService);

    /** Base endpoint for the dealers routes */
    private _baseDealerEndpoint = '/api/dealers';

    /** Base endpoint for users routes */
    private _baseUersEndpont = '/api/users';

    /**
     * Base endpoint for dealer host routes
     */
    private _baseDealerHostEndpoint = '/api/host-dealer-ownerships';

    /**
     * Base endpoint for the client-types routes
     */
    private _baseClientTypeEndpoint = '/api/client-types';

    /**
     * Base endpoint for the user-client-assignments routes
     */
    private _baseClientAssignmentsEndpoint = '/api/user-client-assignments';

    /**
     * Base endpoint for the advertisers routes
     */
    private _baseAdvertisersEndpoint = '/api/advertisers';

    /** Endpoint for getting dealer statistics */
    private _baseDealerStatsEndpoint = '/api/dealers/stats';

    /** Endpoint for getting dealer information */
    private _baseDealerInformationEndpoint = '/api/dealers';

    /**
     * Internal cache for dealer queries.
     */
    private readonly _cache = new Map<string, Observable<DealerCacheType>>();

    /**
     * Retrieves the clientType data for the provided client type.
     * Used to retrieve client-information records for dealers ONLY
     *
     * @param clientType - The client type to retrieve the clientType data for
     *
     * @returns The clientType data which contains the clientTypeId which you can use to filter users based on this id
     *
     * @example
     * ```typescript
     * this.dealerService.getClientType(ClientTypeCode.DEALER).subscribe((res) => {
     *  const clientTypeId = res.data[0].clientTypeId;
     * });
     * ```
     */
    public getClientType(clientType: ClientTypeCode): Observable<ApiGetAllResponse<ApiClientType>> {
        return this._http.get<ApiGetAllResponse<ApiClientType>>(
            `${this._baseClientTypeEndpoint}?filters={"clientTypeCode": {"_eq": "${clientType}"}}`,
        );
    }

    /**
     * Retrieves paginated list of dealers
     *
     * @param params Query parameters for pagination and filtering
     *
     * @returns Observable of paginated list of dealers
     */
    public getAll(params?: BaseQueryParams): Observable<ApiGetAllResponse<ApiDealerInformation>> {
        const queryParams = buildQueryParams(params);
        const cacheKey = `getAll-${JSON.stringify(params || {})}`;

        return this._sharedService.getCached(this._cache, cacheKey, () =>
            this._http.get<ApiGetAllResponse<ApiDealerInformation>>(
                `${this._baseDealerInformationEndpoint}`,
                {
                    params: queryParams,
                },
            ),
        );
    }

    /**
     * Searches for dealers based on a partial or complete match of their alias or business name.
     *
     * @param {string} searchKey - Either the dealer alias, or dealer business name
     * @returns {Observable<ApiGetAllResponse<ApiDealerInformation>>} An observable emitting the API response containing the matched dealers.
     */
    public searchDealers(searchKey: string): Observable<ApiGetAllResponse<ApiDealerInformation>> {
        const queryParams = buildQueryParams({
            searchFields: ['clientInformation.alias', 'clientInformation.businessName'],
            search: searchKey,
        });

        return this._http.get<ApiGetAllResponse<ApiDealerInformation>>(this._baseDealerEndpoint, {
            params: queryParams,
        });
    }

    /**
     * [UPDATED - TOBECHANGED] Retrieves paginated list of dealers
     *
     *  [UPDATE]: This will be the new getAll() method, please update your method calls to use this one
     *  Also update your  `mapDataToUi` to match the structure of the `ApiDealerNew` interface
     *  as the `ApiDealer` interface will be removed soon.
     *
     * @param params Query parameters for pagination and filtering
     */
    public getAllReplacement(
        params?: BaseQueryParams,
    ): Observable<ApiGetAllResponse<ApiDealerInformation>> {
        const queryParams = buildQueryParams(params);
        return this._http.get<ApiGetAllResponse<ApiDealerInformation>>(this._baseDealerEndpoint, {
            params: queryParams,
        });
    }

    /**
     * Retrieves email based on user userId
     * Compare against the user.userId column
     *
     * @param userId - The user id of the dealer
     *
     * @returns Observable of dealer information
     */
    public getDealerByUserId(userId: string): Observable<ApiSingleResponse<ApiDealerInformation>> {
        return this._http.get<ApiSingleResponse<ApiDealerInformation>>(
            `${this._baseUersEndpont}/${userId}`,
        );
    }

    /**
     * Clears the dealer/advertiser cache.
     */
    public clearCache(): void {
        this._cache.clear();
    }

    /**
     * Retrieves a paginated list of hosts for a specific dealer.
     *
     * @description
     * Fetches ownership records from the host-dealer-ownerships endpoint using
     * the provided query parameters for filtering.
     *
     * @param params - Query parameters for pagination and filtering.
     * @returns Observable of paginated host-dealer ownership list.
     */
    public getDealerHosts(params?: BaseQueryParams): Observable<ApiGetAllResponse<ApiDealerHost>> {
        const queryParams = buildQueryParams(params);

        return this._http.get<ApiGetAllResponse<ApiDealerHost>>(`${this._baseDealerHostEndpoint}`, {
            params: queryParams,
        });
    }

    /**
     * Retrieves paginated list of advertisers
     *
     * @param params Query parameters for pagination and filtering
     *
     * @returns Observable of paginated list of advertisers
     */
    public getDealerAdvertisers(
        params?: BaseQueryParams,
    ): Observable<ApiGetAllResponse<ApiDealer>> {
        const queryParams = buildQueryParams(params);
        return this._http.get<ApiGetAllResponse<ApiDealer>>(
            `${this._baseClientAssignmentsEndpoint}`,
            {
                params: queryParams,
            },
        );
    }

    /**
     * Assign eealer to a dealer admin
     *
     * @param dealerIds - The newly selected dealer Ids to assign to a dealer admin
     * @param dealerAdminId - The dealer admin id to assign the dealers to
     * @returns
     */
    public assignDealerToDealerAdmin(
        dealerIds: string[],
        dealerAdminId: string,
    ): Observable<ApiSingleResponse<ApiDealer>> {
        return this._http.post<ApiSingleResponse<ApiDealer>>(
            `${this._baseClientAssignmentsEndpoint}`,
            {
                dealerIds,
                dealerAdminId,
            },
        );
    }

    /**
     * Search advertisers based on the given search key
     * Improve comment later
     *
     * @param searchKey
     * @returns
     */
    public searchAdvertisers(searchKey: string): Observable<any> {
        const queryParams = buildQueryParams({
            search: searchKey,
        });
        return this._http.get<any>(`${this._baseAdvertisersEndpoint}`, {
            params: queryParams,
        });
    }

    // Temporary
    public search(searchKey: string): Observable<Partial<UiDealersAutocomplete>[]> {
        return of(
            [
                { dealerId: 'dealer1', dealerName: 'Dealer 1' },
                { dealerId: 'dealer2', dealerName: 'Dealer 2' },
                { dealerId: 'dealer3', dealerName: 'Dealer 3' },
            ].filter((d) => d.dealerId.toLowerCase().includes(searchKey.toLowerCase())),
        ).pipe(delay(1000));
    }

    // Temporary, fetch dealers by user id (Dealer Admin & Super Admin only)
    public getByUserId(userId: string): Observable<Partial<UiDealersAutocomplete>[]> {
        return of(
            [
                { dealerId: '550e8400-e29b-41d4-a716-446655440005', dealerName: 'Dealer 1' },
                { dealerId: 'dealer2', dealerName: 'Dealer 2' },
                { dealerId: '550e8400-e29b-41d4-a716-4466554400', dealerName: 'Dealer 3' },
            ].filter((d) => d.dealerId.toLowerCase().includes(userId.toLowerCase())),
        ).pipe(delay(1000));
    }

    public getHostsByDealerId(dealerId: string): Observable<Partial<UiDealersAutocomplete>[]> {
        return of(
            [
                { dealerId: '550e8400-e29b-41d4-a716-446655440005', dealerName: 'Dealer 1' },
                { dealerId: 'dealer2', dealerName: 'Dealer 2' },
                { dealerId: '550e8400-e29b-41d4-a716-4466554400', dealerName: 'Dealer 3' },
            ].filter((d) => d.dealerId.toLowerCase().includes(dealerId.toLowerCase())),
        ).pipe(delay(1000));
    }

    public getAdvertisersByDealerId(
        dealerId: string,
    ): Observable<Partial<UiDealersAutocomplete>[]> {
        return of(
            [
                { dealerId: '550e8400-e29b-41d4-a716-446655440005', dealerName: 'Dealer 1' },
                { dealerId: 'dealer2', dealerName: 'Dealer 2' },
                { dealerId: '550e8400-e29b-41d4-a716-4466554400', dealerName: 'Dealer 3' },
            ].filter((d) => d.dealerId.toLowerCase().includes(dealerId.toLowerCase())),
        ).pipe(delay(1000));
    }

    /**
     * @public
     * Gets the dealer statistics for the dealer dashboard cards
     * @returns Observable of dealer statistics
     */
    public getDealerStats(): Observable<ApiSingleResponse<ApiDealerStats>> {
        return this._http.get<ApiSingleResponse<ApiDealerStats>>(
            `${this._baseDealerStatsEndpoint}`,
        );
    }

    /**
     * Retrieves aggregated dealer stats for the dashboard.
     *
     * @returns Observable of dealer stats
     */
    public getStats(): Observable<DealerStats> {
        return this._sharedService.getCached(this._cache, 'stats', () =>
            this._http.get<DealerStats>(`${this._baseDealerStatsEndpoint}`),
        );
    }

    /**
     * Deletes a single dealer by its id.
     *
     * @param dealerId - The dealer id to delete.
     * @returns Observable that completes when the delete succeeds.
     */
    public deleteSingleDealer(dealerId: string): Observable<void> {
        return this._http
            .delete<void>(`${this._baseDealerEndpoint}/${dealerId}`)
            .pipe(tap(() => this.clearCache()));
    }

    /**
     * Deletes multiple dealers by their ids.
     *
     * @param dealerIds - The dealer ids to delete in bulk.
     * @returns Observable that completes when the bulk delete succeeds.
     */
    public deleteBulkDealers(dealerIds: string[]): Observable<void> {
        return this._http
            .post<void>(`${this._baseDealerEndpoint}/bulk-delete`, {
                clientInformationIds: dealerIds,
            })
            .pipe(tap(() => this.clearCache()));
    }

    public getDealers(params?: BaseQueryParams): Observable<ApiDealerListResponse> {
        const queryParams = buildQueryParams(params);
        const cacheKey = `getDealers-${JSON.stringify(params || {})}`;

        return this._sharedService.getCached(this._cache, cacheKey, () =>
            this._http.get<ApiDealerListResponse>(this._baseDealerInformationEndpoint, {
                params: queryParams,
            }),
        );
    }

    /**
     * Loads a dealer row by `clientInformationId` (same id used in `/dealers/:id`).
     */
    public getDealerByClientInformationId(
        clientInformationId: string,
    ): Observable<ApiGetAllResponse<ApiDealerInformation>> {
        const queryParams = buildQueryParams({
            filters: {
                'clientInformation.clientInformationId': {
                    _eq: clientInformationId,
                },
            },
            limit: 1,
        });
        return this._http.get<ApiGetAllResponse<ApiDealerInformation>>(this._baseDealerEndpoint, {
            params: queryParams,
        });
    }

    /**
     * Patches dealer account fields (user profile and/or client information).
     *
     * @param userId - Dealer owner user id (`user.userId`); used as the `/api/dealers/:id` path segment.
     * @param body - Include `user` and/or `clientInformation` with only the fields that changed.
     */
    public patchDealer(userId: string, body: DealerPatchBody): Observable<unknown> {
        return this._http
            .patch<unknown>(`${this._baseDealerEndpoint}/${userId}`, body)
            .pipe(tap(() => this.clearCache()));
    }

    /**
     * Patches client information fields for a dealer account.
     *
     * @param userId - Dealer owner user id (`user.userId`); used as the `/api/dealers/:id` path segment.
     * @param clientInformationDelta - Only include `clientInformation` properties that changed.
     * @deprecated Prefer {@link patchDealer} so user and client updates can be sent together.
     */
    public updateDealerClientInfo(
        userId: string,
        clientInformationDelta: Partial<
            Pick<ApiClientInformation, 'businessName' | 'alias' | 'city' | 'state' | 'region'>
        >,
    ): Observable<unknown> {
        return this.patchDealer(userId, { clientInformation: clientInformationDelta });
    }
}
